<template>
	<div class="cards-field-settings field-settings needs-data-source" :class="{ 'has-data-source': selectedNodeUUID }" v-if="localField">
		<v-expansion-panels v-model="panel" flat accordion>
			<ExpansionPanel :title="'Basics'">
				<SettingSwitch
					:title="'Label'"
					:placeholder="'Type your title here'"
					:switchValue.sync="titleEnabled"
					:textFieldValue.sync="titleValue"
				/>
			</ExpansionPanel>
			<ExpansionPanel :title="'Data'">
				<ol class="field-setting-section">
					<li class="configuration-step choose-data-source">
						<div class="step-title">Choose data source</div>
						<CombinedInputFieldDataMenu
							:value.sync="selectedSource"
							readonly
							hasDelete
							:textAreaMode="false"
							:attachTo="'.choose-data-source'"
							:multipleSelection="false"
							:valuesTypes="dataInputMenuValueTypes"
							:prefixStyle="{ 'margin-left': '4px !important' }"
							:suffixStyle="{ 'margin-right': '10px !important' }"
						/>
						<div class="warning-message" v-if="!selectedNodeHasTestValues">
							You need to test the Data Source in order to fill the placements
						</div>
					</li>
					<li class="configuration-step">
						<div class="step-title">Choose Template</div>
						<TemplateSelector
							:templates="supportedTemplates"
							@onSelectedTemplate="(templateId) => (selectedTemplateId = templateId)"
							:selectedTemplateId="(selectedTemplate || {}).id"
						/>
					</li>

					<li class="configuration-step">
						<div class="step-title">Connect Property</div>
						<div v-if="selectedNodeUUID && selectedTemplate" :key="`${selectedNodeUUID}__${selectedTemplateId}`">
							<ExpandableCard
								class="expandable-card-container"
								v-for="model in placementModels"
								:key="model.key"
								:model="model"
								@openPlacement="openPlacement"
								@updateValue="updateParamMapValue"
								:row="exampleRow(selectedNodePreviousStepsData, selectedPath)"
								:selectedNodeId="selectedNodeId"
								:prefixStyle="{ 'margin-left': '12px !important' }"
								:suffixStyle="{ 'margin-right': '20px !important' }"
							/>
						</div>
						<div class="placeholder-message" v-else>Chooose a Data Source</div>
					</li>
				</ol>
			</ExpansionPanel>
			<ExpansionPanel :title="'Controls'">
				<div class="step-title mb-5">User controls</div>
				<SwitchOption option-name="Find" :switch-model.sync="findEnabled" />
				<SwitchOption option-name="Select" :switch-model.sync="userSelection" />
				<div class="selection-sub-folder" v-if="userSelection">
					<SwitchOption
						v-if="!isSlackTool"
						option-name="Submit on selection"
						:switch-model.sync="isSubmitOnSelectEnabled"
						description="Tip: hide the “footer buttons” for a clean experience"
					/>
					<SwitchOption option-name="Multiple select" :switch-model.sync="multipleSelection" :disabled="isSubmitOnSelectEnabled" />
					<SwitchOption option-name="Required" :switch-model.sync="requiredEnabled" />
				</div>
			</ExpansionPanel>
			<ExpansionPanel v-if="!isSlackTool" :title="'Explanations'">
				<SettingSwitch
					:title="'Description'"
					:placeholder="'Type your description here'"
					:switchValue.sync="descriptionEnabled"
					:textFieldValue.sync="descriptionValue"
				/>
			</ExpansionPanel>
		</v-expansion-panels>
	</div>
</template>
<script>
import TEMPLATES, { slackTemplates } from './templates.const';
import { mapActions, mapGetters } from 'vuex';
import ExpandableCard from './ExpandableCard.vue';
import ParamsService from '@/utils/ParamsService';
import TemplateSelector from './TemplateSelector.vue';
import FieldSettingsMixin, { getComputedMetadataOption } from '../FieldSettingsMixin';
import SettingSwitch from '../WidgetSettings/SettingSwitch';
import ExpansionPanel from '../WidgetSettings/ExpansionPanel';
import { nodesDataByUUID, replaceInsertDataStringDictionary, isOfTypeSlack } from '@/store/modules/selectedWorkflow/getters';
import { getCurrentEditedPlacementForCard } from '@/store/modules/cardsSettings/getters';
import CombinedInputFieldDataMenu from '@/components/DataInput/CombinedInputFieldDataMenu';
import { setCurrentlyEditedPlacementIndex, resetCurrentEditedPlacementIndex } from '@/store/modules/cardsSettings/actions';
import TableOnlyAllowedValueTypesMixin from '../ValueTypesMixins/TableOnlyAllowedValueTypesMixin';
import TableRelatedFieldSettingsMixin from '../TableRelatedFieldSettingsMixin';
import { getPreviousStepDataForNodeByUUID } from '@/store/modules/nodes/getters';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';

export default {
	name: 'CardsFieldSettings',
	components: {
		SettingSwitch,
		ExpansionPanel,
		ExpandableCard,
		TemplateSelector,
		CombinedInputFieldDataMenu,
	},
	mixins: [FieldSettingsMixin, TableOnlyAllowedValueTypesMixin, TableRelatedFieldSettingsMixin],
	computed: {
		...mapGetters([
			getCurrentEditedPlacementForCard.fullPath,
			nodesDataByUUID.fullPath,
			getPreviousStepDataForNodeByUUID.fullPath,
			replaceInsertDataStringDictionary.fullPath,
			configurationsMappingBlocksUUIDs.fullPath,
			isOfTypeSlack.fullPath,
		]),
		isSlackTool() {
			return this[isOfTypeSlack.fullPath];
		},
		supportedTemplates() {
			if (this.isSlackTool) return slackTemplates;
			return TEMPLATES;
		},
		isSubmitOnSelectEnabled: getComputedMetadataOption('submit_on_select', 'enabled'),
		selectedNodeUUID() {
			return ParamsService.extractNodeUUIDFromValue(this.selectedSource);
		},
		selectedNodeId() {
			return this.selectedNodeData.id;
		},
		selectedPath() {
			return ParamsService.extractPathFromValue(
				this.selectedSource,
				this[replaceInsertDataStringDictionary.fullPath],
				this[configurationsMappingBlocksUUIDs.fullPath]
			);
		},
		selectedSource: {
			get() {
				return this.localField.metadata.options.step_id.value;
			},
			set(value) {
				const hasChanged = this.selectedSource !== value;
				this.setLocalFieldKey('metadata.options.step_id', { value });
				if (hasChanged) {
					this.paramsMap = {}; // reset the paramsMap when changing selected node
					this.selectedTemplate = null; // reset and make them choose again
				}
			},
		},
		selectedTemplate: {
			get() {
				return this.localField.metadata.options.template.value;
			},
			set(value) {
				this.paramsMap = {}; // reset the paramsMap when changing selected node
				this.setLocalFieldKey('metadata.options.template', { value });
				this.getSelectedTemplatePlacements(value).forEach((p) => (this.paramsMap[p.id] = { value: '' }));
			},
		},
		selectedNodeData() {
			return this[nodesDataByUUID.fullPath][this.selectedNodeUUID];
		},
		selectedNodePreviousStepsData() {
			return this[getPreviousStepDataForNodeByUUID.fullPath](this.selectedNodeUUID);
		},
		selectedNodeHasTestValues() {
			return Object.keys(this.selectedNodeData ? this.selectedNodeData.test_result || {} : {}).length > 0;
		},
		placementModels() {
			return this.getSelectedTemplatePlacements(this.selectedTemplate).map((placement) => ({
				key: placement.id,
				data: this.paramsMap[placement.id],
				isOpen: this.openPlacementKey === placement.id,
			}));
		},
		paramsMap: {
			get() {
				return this.localField.metadata.params_map;
			},
			set(value) {
				this.setLocalFieldKey('metadata.params_map', value);
			},
		},
		userSelection: {
			get() {
				return this.localField.metadata.options.user_selection.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.user_selection.enabled', value);
				if (!value) {
					this.multipleSelection = false;
					this.requiredEnabled = false;
				}
			},
		},
		multipleSelection: {
			get() {
				return this.localField.metadata.options.user_selection.multiple;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.user_selection.multiple', value);
			},
		},
		selectedTemplateId: {
			get() {
				return ((this.localField.metadata.options.template || {}).value || {}).id;
			},
			set(value) {
				this.selectedTemplate = this.supportedTemplates[value];
			},
		},
		openPlacementKey: {
			get() {
				return this[getCurrentEditedPlacementForCard.fullPath](this.localField.id);
			},
			set(value) {
				this[setCurrentlyEditedPlacementIndex.fullPath]({
					fieldId: this.localField.id,
					placementKey: this.openPlacementKey === value ? null : value,
				});
			},
		},
		findEnabled: {
			get() {
				return this.localField.metadata.options.find.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.find.enabled', value);
			},
		},
	},
	methods: {
		...mapActions([setCurrentlyEditedPlacementIndex.fullPath, resetCurrentEditedPlacementIndex.fullPath]),
		getSelectedTemplatePlacements(template) {
			const bodyRows = (template?.body || []).reduce((result, currentRow) => [...result, ...currentRow], []);
			const allPlacements = [...(template?.header || []), ...bodyRows];
			return allPlacements;
		},
		openPlacement(key) {
			this.openPlacementKey = this.openPlacementKey === key ? null : key;
		},
		updateParamMapValue(payload) {
			const { key, value } = payload;
			const newParamsMap = { ...this.paramsMap };
			newParamsMap[key] = value;
			this.paramsMap = newParamsMap;
		},
	},
	data() {
		return {
			currentlyEdited: { currentEditedPlacement: null },
			TEMPLATES,
		};
	},
	watch: {
		isSubmitOnSelectEnabled: {
			handler(newV) {
				if (newV) {
					this.multipleSelection = false;
				}
			},
			immediate: true,
		},
	},
	mounted() {
		this[resetCurrentEditedPlacementIndex.fullPath](); // reset old edited placement index
	},
};
</script>
